import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Iconify from '../../components/iconify';
import CmsChatbot from './chatbot';

const MultiChatbot = ({ type = 'simple' }) => {
  const [chatbots, setChatbots] = useState([
    { id: 1, type, minimized: false }
  ]);
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeChat, setActiveChat] = useState(chatbots.length > 0 ? chatbots[0].id : null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const chatbotRefs = useRef({});
  
  const handleOpenChat = () => {
    setIsOpen(true);
    
    if (activeChat === null && chatbots.length > 0) {
      setActiveChat(chatbots[0].id);
    }
  };
  
  const handleCloseChat = () => {
    setIsOpen(false);
  };
  
  const handleToggleMinimize = (id) => {
    setChatbots(chatbots.map(bot => 
      bot.id === id 
        ? { ...bot, minimized: !bot.minimized } 
        : bot
    ));
  };
  
  const handleAddChatbot = () => {
    const newId = chatbots.length > 0 
      ? Math.max(...chatbots.map(bot => bot.id)) + 1 
      : 1;
    
    const newBot = { id: newId, type, minimized: false };
    setChatbots([...chatbots, newBot]);
    setActiveChat(newId);
    setIsOpen(true);
  };
  
  const handleCloseChatbot = (id) => {
    const updatedChatbots = chatbots.filter(bot => bot.id !== id);
    setChatbots(updatedChatbots);
  
    if (activeChat === id) {
      const newActiveChat = updatedChatbots.length > 0 ? updatedChatbots[0].id : null;
      setActiveChat(newActiveChat);
    }
  };
  
  
  const handleChangeChat = (event, newValue) => {
    setActiveChat(newValue);
    setIsOpen(true);
    
    setChatbots(chatbots.map(bot => 
      bot.id === newValue 
        ? { ...bot, minimized: false } 
        : bot
    ));
  };
  
  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };
  
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 2,
        maxWidth: {
          xs: 'calc(100vw - 16px - 16px)', // スマートフォン
          sm: '450px', // タブレット以上
        },
        width: '100%',
        height: isOpen ? 'calc(100svh - 80px - 16px)' : 'auto',
      }}
    >
      {/* コントロールボタン */}
      <Stack 
        direction="row" 
        spacing={1} 
        sx={{ 
          width: '100%', 
          justifyContent: 'flex-end',
          display: isOpen ? 'none' : 'flex',
          py: 2,
          px: 1,
        }}
      >
        
        {/* チャット開閉ボタン */}
        {!isOpen && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Iconify icon="eva:message-square-fill" />}
            onClick={handleOpenChat}
            sx={{
              borderRadius: '23px',
              height: '46px',
              px: 3,
            }}
          >
            チャットを開く(α版)
          </Button>
        )}
      </Stack>
      
      {/* チャットリストメニュー */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        sx={{
          '& .MuiPaper-root': {
            width: 250,
            maxWidth: '100%',
            mt: 1.5,
            boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.48)',
            zIndex: 1500,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        style={{ zIndex: 1500 }}
      >
        {chatbots.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="チャットがありません" />
          </MenuItem>
        ) : (
          chatbots.map((bot) => (
            <MenuItem 
              key={bot.id} 
              onClick={() => {
                handleChangeChat(null, bot.id);
                handleCloseMenu();
              }}
              selected={activeChat === bot.id}
            >
              <ListItemIcon>
                <Iconify icon="eva:message-circle-fill" width={24} />
              </ListItemIcon>
              <ListItemText primary={`チャット ${bot.id}`} />
              <Button
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseChatbot(bot.id);
                  if (chatbots.length <= 1) {
                    handleCloseMenu();
                  }
                }}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                <Iconify icon="eva:close-fill" width={16} />
              </Button>
            </MenuItem>
          ))
        )}
      </Menu>
      
      {/* チャットウィンドウ */}
      <Paper 
        sx={{ 
          flexGrow: 1,
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.24)',
          position: 'relative',
          zIndex: 1000,
          display: isOpen ? 'flex' : 'none',
          height: '100%',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={activeChat || 1}
          onChange={handleChangeChat}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: 'grey.300',
            '& .MuiTab-root': {
              minWidth: 100,
              px: 2,
              py: 1,
              mr: 0,
            },
            '& .MuiTab-root.Mui-selected': {
              backgroundColor: '#ffffff',
            },
            '& .MuiTab-root:not(:last-of-type)': {
              mr: 0,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'secondary.main',
              bottom: "auto",
              top: 0,
            },
          }}
        >
          {chatbots.map((bot) => (
            <Tab 
              key={bot.id} 
              value={bot.id} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <span>{`チャット ${bot.id}`}</span>
                  <Box 
                    component="span" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseChatbot(bot.id);
                    }}
                    sx={{ 
                      ml: 1, 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      color: 'text.secondary',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'secondary.main',
                        color: 'white',
                      },
                    }}
                  >
                    <Iconify icon="eva:close-fill" width={16} />
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
        
        {/* アクティブなチャットのみ表示 */}
        {chatbots.map((bot) => (
          <Box 
            key={bot.id} 
            sx={{ 
              display: activeChat === bot.id ? 'block' : 'none',
              position: 'relative',
            }}
          >
            <CmsChatbot
              ref={(el) => {
                chatbotRefs.current[bot.id] = el;
              }}
              type={bot.type}
              def_minimized={bot.minimized}
              show_chat_button={false}
              headerText={`チャット ${bot.id}`}
            />
          </Box>
        ))}
        {/* チャット追加ボタン */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 1,
            py: 2,
            mt: 'auto',
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAddChatbot}
            sx={{
              borderRadius: '23px',
              height: '46px',
              px: 2,
            }}
          >
            チャット追加
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Iconify icon="eva:close-square-fill" />}
            onClick={handleCloseChat}
            sx={{
              borderRadius: '23px',
              height: '46px',
              px: 2,
            }}
          >
            閉じる
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

MultiChatbot.propTypes = {
  type: PropTypes.string,
};

export default MultiChatbot;
